// Quasar styles and extras
import { i18n, useTranslator } from '@juit/vue-i18n'
import { JuitWidgets } from '@juit/vue-z'
import { Loading, Notify } from 'quasar'
import { createApp } from 'vue'

import App from '@/app.vue'
import { translations } from '@/assets/translations'
import { client } from '@/init/client'
import { router } from '@/init/router'
import { setupSentry } from '@/init/sentry'
// Global styles
import '@/assets/fonts/font.css'
import '@/classes.scss'

import type { DateTimeFormats } from '@juit/vue-i18n'

/* ========================================================================== *
 * Initialize and mount app                                                   *
 * ========================================================================== */

// Override the default date formats
const dateTimeFormats = {
  // Default date/time format:
  // * EN: 17/02/2025, 15:57:33
  // * DE: 17.02.2025, 15:57:33
  default: {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  },
  // Long format (e.g. for object info)
  // * EN: Monday 17 February 2025 at 15:57:33
  // * DE: Montag, 17. Februar 2025 um 15:57:33
  long: {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    weekday: 'long',
  },
  // Date-only format (e.g. transfer date, or our date picker
  // * EN: Mon, 17/02/2025
  // * DE: Mo., 17.02.2025
  date: {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    weekday: 'short',
  },
  // Date-only format (e.g. transfer date, or our date picker
  // * EN: Mon, 17/02/2025
  // * DE: Mo., 17.02.2025
  short: {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  },
} as const satisfies DateTimeFormats

// I18N Types Configuration
declare module '@juit/vue-i18n' {
  export interface I18nConfiguration {
    languages: 'de' | 'en'
    translationKeys: keyof typeof translations
    dateTimeFormats: keyof typeof dateTimeFormats
    numberFormats: never // keeps defaults only
  }
}

// Create app
const myApp = createApp(App)

// Initialize Sentry
if ((window.location.hostname !== 'localhost') &&
    (window.location.hostname !== '127.0.0.1')) {
  setupSentry(myApp)
}

// Initialize app
myApp.use(router)
myApp.use(i18n, {
  defaultLanguage: 'en-GB',
  defaultTimeZone: 'Europe/Berlin',
  translations,
  dateTimeFormats,
})
myApp.use(JuitWidgets, {
  plugins: { Notify, Loading }, // quasar plugins (Dialog is pre-included)
})

client.on('user', (user) => {
  myApp.runWithContext(() => {
    const translator = useTranslator()
    switch (user?.locale) {
      case 'de': translator.locale = new Intl.Locale('de-DE'); break
      case 'en': translator.locale = new Intl.Locale('en-GB'); break
    }
  })
})

// Mount app after the user (if any) is fetched
client.getUser().then(() => myApp.mount('#app'))
